import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { PaymentsInvoiceUpcoming, PaymentsProducts } from "~/api/Api";
import { PaymentsProduct } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { useTeam } from "~/contexts/TeamProvider";
import {
  AI_META_DESCRIPTION_PRODUCT_KEY,
  AI_META_TITLE_PRODUCT_KEY,
  AI_SCHEMA_PRODUCT_KEY,
  KEYWORD_RANK_PRODUCT_KEY,
  PAGE_OPTIMIZATION_PRODUCT_KEY,
} from "~/helpers/stripe_consts";
import { tryCatch } from "~/helpers/tryCatch";

import { BillingCharts } from "./BillingCharts";
import { BillingHeader } from "./BillingHeader";
import styles from "./BillingOverview.module.scss";
import { BillingTable } from "./BillingTable";
import { BillingTotals } from "./BillingTotals";
import { AiEngineData, CrawlRankingData, LineItem } from "./types";

interface BillingOverviewProps {
  billingData: PaymentsInvoiceUpcoming;
}

type PeriodTuple = [string, LineItem[]];

function findProductByKey(products: PaymentsProducts, key: string) {
  return products.find((p: PaymentsProduct) => p.key === key);
}

const chartColors = [
  "var(--chart2-green)",
  "var(--functional-primary)",
  "var(--chart3-yellow)",
];

const sumAmounts = (items: LineItem[]) =>
  items.reduce((acc, item) => acc + (item.amount ?? 0), 0);

function chartColor(i: number) {
  return chartColors[i % chartColors.length];
}

function chartData(item: LineItem, index: number) {
  return {
    feature: item.name,
    quantity: item.quantity,
    unitPrice: item.unit_amount ?? 0,
    amount: item.amount ?? 0,
    color: chartColor(index),
  };
}

export const BillingOverview = ({ billingData }: BillingOverviewProps) => {
  const { teamId } = useTeam();

  // The products only group the invoice lines. The catalog depends on the
  // team, and the backend closes it to some (an unpaid Autopto agency still
  // sees its invoices): the lines then go ungrouped.
  const { data: products = [] } = useQuery({
    queryKey: ["invoiceAddonProducts", teamId],
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.billing.productsList({ q: { kind_eq: "add-on" } }),
      );

      if (result.error?.status === 403) return [];
      if (result.error) throw result.error;

      return result.data.data?.["payments/products"] ?? [];
    },
  });

  const keywordRankProduct = findProductByKey(
    products,
    KEYWORD_RANK_PRODUCT_KEY,
  );
  const pageCrawlProduct = findProductByKey(
    products,
    PAGE_OPTIMIZATION_PRODUCT_KEY,
  );
  const aiMetaTitleProduct = findProductByKey(
    products,
    AI_META_TITLE_PRODUCT_KEY,
  );
  const aiMetaDescriptionProduct = findProductByKey(
    products,
    AI_META_DESCRIPTION_PRODUCT_KEY,
  );
  const aiSchemaProduct = findProductByKey(products, AI_SCHEMA_PRODUCT_KEY);

  const flatItems: LineItem[] = (
    billingData.grouped_line_items as PeriodTuple[]
  ).flatMap(([, items]) => items);

  const crawlRankingItems: LineItem[] = flatItems.filter((item: LineItem) =>
    [keywordRankProduct, pageCrawlProduct].some(
      (p) => p?.id === item.product_id,
    ),
  );

  const crawlRankingChartData = useMemo<CrawlRankingData>(
    () => crawlRankingItems.map((item, i) => chartData(item, i)),
    [crawlRankingItems],
  );

  const aiEngineChartItems: LineItem[] = flatItems.filter((item: LineItem) =>
    [aiMetaTitleProduct, aiMetaDescriptionProduct, aiSchemaProduct].some(
      (p) => p?.id === item.product_id,
    ),
  );

  const aiEngineChartData = useMemo<AiEngineData>(
    () => aiEngineChartItems.map((item, i) => chartData(item, i)),
    [aiEngineChartItems],
  );

  const hasAnyChart =
    crawlRankingChartData.length > 0 || aiEngineChartData.length > 0;
  const hasBothCharts =
    crawlRankingChartData.length > 0 && aiEngineChartData.length > 0;

  return (
    <main className={styles.billing}>
      <BillingHeader billedDate={billingData.date} />
      {/*
       // @ts-expect-error TS2551 non-existing className */}
      <div className={styles.billing__content}>
        {hasAnyChart && (
          // @ts-expect-error TS2551 non-existing className
          <div className={styles.billing__left}>
            <BillingCharts
              crawlRankingData={crawlRankingChartData}
              aiEngineData={aiEngineChartData}
            />
          </div>
        )}
        {/*
         // @ts-expect-error TS2551 non-existing className */}
        <div className={styles.billing__right}>
          <BillingTotals
            fullWidth={!hasBothCharts}
            amountDue={billingData.amount_due}
            total={billingData.total}
            crawlRanking={sumAmounts(crawlRankingItems)}
            aiUsage={sumAmounts(aiEngineChartItems)}
            lastUpdate={billingData.current_datetime}
          />
        </div>
      </div>
      <BillingTable
        /*dirty workaround as currently backend does not support OpenAPI 3.1 with pseudo tuples (prefixItems option) */
        lineItems={
          billingData.grouped_line_items as unknown as [string, LineItem[]][]
        }
        discounts={billingData.discounts}
      />
    </main>
  );
};
